/**
 * StatusFilter Component
 * Dropdown-style multi-select filter for order statuses
 * Allows filtering orders by multiple status values
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 2
 */

import * as React from 'react';
import { Check, ChevronDown, Zap } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { OrderStatus } from '@/types/order';

// ============================================
// CONSTANTS
// ============================================

/**
 * Status configuration with display labels and colors
 */
const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; badgeClass: string }> = {
  PENDING: {
    label: 'Pending',
    color: 'text-yellow-700',
    badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  CONFIRMED: {
    label: 'Confirmed',
    color: 'text-blue-700',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-300',
  },
  IN_PROGRESS: {
    label: 'Processing',
    color: 'text-purple-700',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-300',
  },
  DELIVERED: {
    label: 'Delivered',
    color: 'text-green-700',
    badgeClass: 'bg-green-100 text-green-800 border-green-300',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-teal-700',
    badgeClass: 'bg-teal-100 text-teal-800 border-teal-300',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-red-700',
    badgeClass: 'bg-red-100 text-red-800 border-red-300',
  },
};

/**
 * Order of statuses to display
 */
const STATUS_ORDER: OrderStatus[] = [
  'PENDING',
  'CONFIRMED',
  'IN_PROGRESS',
  'DELIVERED',
  'COMPLETED',
  'CANCELLED',
];

// ============================================
// INTERFACE
// ============================================

export interface StatusFilterProps {
  selectedStatuses: OrderStatus[];
  onStatusChange: (statuses: OrderStatus[]) => void;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const StatusFilter = React.memo(function StatusFilter({
  selectedStatuses,
  onStatusChange,
  className,
}: StatusFilterProps) {
  // Handle individual status toggle
  const handleStatusToggle = React.useCallback(
    (status: OrderStatus) => {
      const isSelected = selectedStatuses.includes(status);
      const newStatuses = isSelected
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses, status];
      onStatusChange(newStatuses);
    },
    [selectedStatuses, onStatusChange]
  );

  // Handle select all
  const handleSelectAll = React.useCallback(() => {
    if (selectedStatuses.length === STATUS_ORDER.length) {
      onStatusChange([]);
    } else {
      onStatusChange([...STATUS_ORDER]);
    }
  }, [selectedStatuses.length, onStatusChange]);

  const isAllSelected = selectedStatuses.length === STATUS_ORDER.length;
  const isIndeterminate = selectedStatuses.length > 0 && selectedStatuses.length < STATUS_ORDER.length;

  // Get display text
  const displayText = React.useMemo(() => {
    if (selectedStatuses.length === 0) return 'All Status';
    if (selectedStatuses.length === 1) {
      const status = selectedStatuses[0];
      return STATUS_CONFIG[status].label;
    }
    return `${selectedStatuses.length} Selected`;
  }, [selectedStatuses]);

  return (
    <DropdownMenu>
      <Button
        variant="outline"
        size="sm"
        className={cn(
          'h-9 px-3 py-2 text-sm font-medium w-full justify-between',
          'hover:bg-accent/50 transition-colors'
        )}
      >
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-muted-foreground" />
          <span>{displayText}</span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
      </Button>

      <DropdownMenuContent className="w-56" align="start">
        <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {STATUS_ORDER.map((status) => {
          const config = STATUS_CONFIG[status];
          const isChecked = selectedStatuses.includes(status);

          return (
            <DropdownMenuCheckboxItem
              key={status}
              checked={isChecked}
              onCheckedChange={() => handleStatusToggle(status)}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2 flex-1">
                <Badge
                  variant="outline"
                  className={cn('text-xs font-medium px-1.5 py-0', config.badgeClass)}
                >
                  {config.label}
                </Badge>
              </div>
              {isChecked && <Check className="h-4 w-4" />}
            </DropdownMenuCheckboxItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onStatusChange(selectedStatuses.length === STATUS_ORDER.length ? [] : [...STATUS_ORDER])}
          className="cursor-pointer"
        >
          {selectedStatuses.length === STATUS_ORDER.length ? 'Clear All' : 'Select All'}
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* Accessibility hint */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {selectedStatuses.length === 0
          ? 'No status filters selected. All orders will be shown.'
          : `${selectedStatuses.length} status ${selectedStatuses.length === 1 ? 'filter' : 'filters'} selected.`}
      </span>
    </DropdownMenu>
  );
});
